import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import useRedditAdvertiserMutator, {
	MutatingNewAdvertiser,
} from 'wb-reddit/app/routes/advertisers/hooks/useRedditAdvertiserMutator';
import {
	alephToast,
	Button,
	ButtonAppearance,
	ButtonColor,
	CheckIcon,
	Input,
	isReddit,
	isSpotify,
	LoaderBars,
	NotificationToast,
	NotificationType,
	getNewAvatar,
	getRandomMainColor,
	handleErrorNotification,
} from '@aleph/shared-components';
import React, { useEffect, useMemo, useRef } from 'react';
import ProfileImageUploader from 'wb-reddit/app/common/components/profileImageUploader';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
	AdvertiserOwnerOptionsAutocompleteDocument,
	AgencyAccessOptionsQueryItems,
	AgencyAccessOptionsQueryUsersUsers,
	CreateRedditAdvertiserDocument,
	RedditAdvertiserListDocument,
	UserAccountLevel,
} from 'wb-reddit/graphql/generated';
import debounce from 'debounce-promise';
import AgencyAccessListItem from 'wb-reddit/app/routes/advertisers/mutator/accessListItem';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { object, string } from 'yup';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { useNavigate } from 'react-router-dom';
import { mapFormGraphqlErrors, userHasOneOfRoles } from 'wb-reddit/app/routes/utils';
import { MutatorProps } from 'wb-reddit/app/routes/advertisers/mutator/container';
import AdvertisterOwnerAutocomplete from 'wb-reddit/app/routes/advertisers/mutator/advertisterOwnerAutocomplete';
import AdvertiserAccessAutocomplete from 'wb-reddit/app/routes/advertisers/mutator/advertiserAccessAutocomplete';
import {
	AuthGraphAdvertiserListDocument,
	AuthGraphCreateAdvertiserDocument, AuthGraphGrantAdvertiserAccountAccessDocument,
	AuthGraphSearchOwnerDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import { captureException } from '@sentry/react';

const RedditAdvertiserMutator = ({ beforeClose, updateRow, setShouldShowSaveBeforeExit }: MutatorProps) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { mutatingAdvertiser, setMutatingAdvertiser } = useRedditAdvertiserMutator();
	const oldOwner = useMemo(() => mutatingAdvertiser?.agencyAccountOwner, []);
	const mutatingNewAdvertiser = mutatingAdvertiser as MutatingNewAdvertiser;

	const [searchOwners] = useLazyQuery(
		isReddit()
			? AdvertiserOwnerOptionsAutocompleteDocument
			: AuthGraphSearchOwnerDocument,
	);

	const [grantAdvertiserAccountAccess, { loading: grantAccessLoading }] = useMutation(AuthGraphGrantAdvertiserAccountAccessDocument, {
		errorPolicy: 'all',
	});

	const [createAdvertiser, { loading: creating }] = useMutation(CreateRedditAdvertiserDocument, {
		errorPolicy:         'all',
		refetchQueries:      [RedditAdvertiserListDocument],
		onCompleted:         (_, clientOptions) => clientOptions?.client?.resetStore(),
		awaitRefetchQueries: true,
	});

	const [authCreateAdvertiser, { loading: authCreating }] = useMutation(AuthGraphCreateAdvertiserDocument, {
		errorPolicy:         'all',
		refetchQueries:      [AuthGraphAdvertiserListDocument],
		onCompleted:         (_, clientOptions) => clientOptions?.client?.resetStore(),
		awaitRefetchQueries: true,
	});

	const debouncedSearchOwners = useMemo(() => debounce(searchOwners, 300), []);

	const platformUser = useUserMetaData();

	const loadOwnerOptions = async (search: string) => {
		if (!search) return [];

		if (isReddit() && oldOwner?.userEmail && oldOwner?.userEmail?.split('@')?.[1] !== search?.split('@')?.[1]) {
			return [];
		}

		const { data, error } = await debouncedSearchOwners({
			variables: {
				search,
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: something went wrong while searching for users',
					'Something went wrong while searching for users',
				),
			);

			return [];
		}

		if (isSpotify()) {
			// @ts-ignore
			return data?.users?.items
				?.map(item => ({ ...item, label: `${item?.userName} ${item?.userSurname || ''}` })) ?? [];
		}

		return data?.users ?? [];
	};

	const {
		values,
		submitForm,
		setErrors,
		setFieldValue,
		handleBlur,
		handleChange,
		errors,
		touched,
		dirty,
		setFieldTouched,
		submitCount,
	} = useFormik({
		initialValues:    mutatingNewAdvertiser,
		validationSchema: object().shape({
			agencyAccountName:  string().required(t('re: advertiser.validation.name', 'Advertiser name is required.')),
			agencyAccountOwner: object().required(t('re: advertiser.validation.owner', 'Advertiser owner is required.')),
		}),
		initialTouched: {
			agencyAccountProfilePicture: !!mutatingNewAdvertiser?.agencyAccountProfilePicture,
		},
		onSubmit: async (submitValue) => {
			const { errors: createErrors, data } = isReddit()
				? await createAdvertiser({
					variables: {
						agencyAccountName:           submitValue.agencyAccountName,
						agencyAccountOwnerId:        submitValue.agencyAccountOwner?.userId ?? '',
						agencyAccountProfilePicture: submitValue.agencyAccountProfilePicture?.file ?? undefined,
						salesforceId:                submitValue.salesforceId,
						userIds:                     submitValue.users?.map(user => user?.user?.userId) ?? [],
						userGroupIds:                submitValue.userGroups?.map(group => group.userGroupId) ?? [],
					},
				})
				: await authCreateAdvertiser({
					variables: {
						advertiserAccountName:    submitValue.agencyAccountName,
						advertiserAccountOwnerId: submitValue.agencyAccountOwner?.userId ?? '',
						appId:                    import.meta.env.VITE_APP_ID,
					},
				});

			const advertiserAccountAccess = [];

			if (createErrors) {
				const validationErrors = mapFormGraphqlErrors(createErrors);

				if (validationErrors?.agencyAccountOwnerId) {
					// @ts-ignore: backend type is not according to gql standard
					const agencyNames = createErrors?.slice?.(-1)?.[0]?.agencyName;

					setErrors({
						agencyAccountOwner: `${t(validationErrors?.agencyAccountOwnerId)} ${
							agencyNames?.length > 0
								? `(${agencyNames.join(',')})`
								: ''
						}`,
					});
				}
			}

			if (createErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							t(
								're: something went wrong while creating the advertiser',
								'Something went wrong while creating the advertiser',
							)
						}
					/>,
				);

				return;
			}

			// @ts-ignore
			if (isSpotify() && submitValue?.userGroups?.length) {
				// @ts-ignore
				// eslint-disable-next-line no-restricted-syntax
				for (const group of submitValue.userGroups) {
					try {
						// eslint-disable-next-line no-await-in-loop
						const { data: accessData } = await grantAdvertiserAccountAccess({
							variables: {
								userGroupId:         group.userGroupId,
								// @ts-ignore
								advertiserAccountId: data?.createAdvertiserAccount?.id ?? '',
								appId:               import.meta.env.VITE_APP_ID,
							},
						});

						// @ts-ignore
						advertiserAccountAccess.push(accessData?.grantAdvertiserAccountAccess?.userGroup);
					} catch (error) {
						captureException(error);

						// Show toast
						alephToast(
							<NotificationToast
								type={NotificationType.Error}
								title="Failed to grant access to group"
							/>,
						);
					}
				}
			}

			// @ts-ignore
			if (data?.createAgencyAccount || data?.createAdvertiserAccount) {
				updateRow?.({
					// @ts-ignore
					row: {
						// @ts-ignore
						...(data?.createAgencyAccount || data?.createAdvertiserAccount),
						advertiserAccountAccess: [
							// @ts-ignore
							...(data?.createAdvertiserAccount?.advertiserAccountAccess ?? []),
							advertiserAccountAccess,
						],
					},
					isCreate: true,
				});
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<Trans
							i18nKey="re: advertiser created success notification"
							defaults="Advertiser <strong>{{agencyAccountName}}</strong> successfully created."
							values={{
								agencyAccountName: submitValue.agencyAccountName,
							}}
						>
							Advertiser <strong>{submitValue.agencyAccountName}</strong> successfully created.
						</Trans>
					}
				/>,
			);

			navigate('/advertisers');
			setMutatingAdvertiser(undefined);
		},
	});

	const userCanChangeOwnership = platformUser?.id === oldOwner?.id
	|| userHasOneOfRoles(platformUser, [UserAccountLevel.Admin]);

	const agencyOwner = isReddit() ? values?.agencyAccountOwner : true;

	const onAccessEntitySelected = async (option: AgencyAccessOptionsQueryItems | AgencyAccessOptionsQueryUsersUsers) => {
		if (isSpotify()) {
			setFieldValue('userGroups', [...values.userGroups ?? [], option]);

			return;
		}

		if (option?.__typename === 'SearchUser') {
			setFieldValue('users', [...values.users ?? [], {
				__typename: 'AgencyUser',
				user:       {
					...option,
					// TODO: This will be removed when backend adds username and surname to SearchUser type
					userName:    option?.userFullName?.split(' ')?.[0] ?? '',
					userSurname: option?.userFullName?.split(' ')?.[1] ?? '',
				},
			}]);
		}

		if (option?.__typename === 'UserGroup') {
			setFieldValue('userGroups', [...values.userGroups ?? [], option]);
		}
	};

	useEffect(() => {
		setMutatingAdvertiser(values);
	}, [values]);

	const avatarColor = useRef(getRandomMainColor());

	useEffect(() => {
		if (!touched.agencyAccountProfilePicture) {
			getNewAvatar({
				entityType: 'Advertiser', background: avatarColor.current, size: 167, iconSize: 167,
			})
				.then((newImage) => {
					setFieldValue('agencyAccountProfilePicture', newImage);
				});
		}
	}, [values.agencyAccountName]);

	useEffect(
		() => setShouldShowSaveBeforeExit(dirty),
		[dirty],
	);

	const anyInvalidTouched = () => Object.keys(touched).some((key) => touched[key] && errors[key]);
	const showDiscard = (touched.agencyAccountName || touched.salesforceId);
	const loading = creating || authCreating || grantAccessLoading;

	return (
		<div
			className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]"
		>
			<div className="flex flex-1 justify-center w-[25%]">
				<ProfileImageUploader
					profileImage={values.agencyAccountProfilePicture}
					onUploadProfileImage={image => setFieldValue('agencyAccountProfilePicture', image)}
					type='Advertiser'
				/>
			</div>

			<div className="flex flex-1 flex-col gap-[24px] min-w-[50%] min-h-[100%] px-[2px]">
				<div className="font-semibold text-[14px]">{t('re: advertise general information title', 'GENERAL INFORMATION')}</div>

				<Input
					id="agencyAccountName"
					className="w-full"
					label={t('re: advertiser name', 'Advertiser name')}
					value={values.agencyAccountName}
					onChange={handleChange}
					onBlur={handleBlur}
					validationError={touched.agencyAccountName ? errors.agencyAccountName : undefined}
				/>

				<div>
					<AdvertisterOwnerAutocomplete
						// @ts-ignore
						loadOptions={loadOwnerOptions}
						values={values}
						onOptionClick={(option) => setFieldValue('agencyAccountOwner', option)}
						disabled={!userCanChangeOwnership}
						setTouched={() => setFieldTouched('agencyAccountOwner', true)}
					/>

					{(touched.agencyAccountOwner || submitCount > 0) && errors.agencyAccountOwner
							&& <div className="text-red-500 text-[10px] px-[8px]">{errors.agencyAccountOwner}</div>
					}
				</div>

				{isReddit() && (
					<Input
						id="salesforceId"
						label={t('re: salesforce customer id', 'Salesforce customer ID')}
						value={values.salesforceId ?? ''}
						onChange={handleChange}
						onBlur={handleBlur}
						validationError={touched.agencyAccountName ? errors.salesforceId
							: undefined}
					/>
				)}

				<div className="font-semibold text-[14px] pt-[16px]">{t('re: advertiser access title', 'ACCESS')}</div>

				<AdvertiserAccessAutocomplete
					disabled={!agencyOwner}
					onOptionClick={onAccessEntitySelected}
					selectedGroupIds={values?.userGroups?.map(group => group.userGroupId) ?? []}
				/>

				<div>
					<div className="flex h-[32px] items-center text-gray-600 px-[8px] text-[11px] border-b
							border-b-gray-200"
					>
						<div className="flex-1">{t('re: advertiser access table contributor header', 'CONTRIBUTOR')}</div>
						<div className="px-[8px]">{t('re: advertiser access table domain header', 'DOMAIN')}</div>
						<div className="w-[50px] flex justify-center">{t('re: advertiser access table revoke header', 'REVOKE')}</div>
					</div>

					{values.agencyAccountOwner && (
						<AgencyAccessListItem
							entity={{
								...values.agencyAccountOwner,
								isOwner: true,
							}}
						/>
					)
					}

					{values.userGroups?.map(group => (
						<AgencyAccessListItem
							key={group.id}
							entity={group}
							onRemove={() => {
								setFieldValue(
									'userGroups',
									(values.userGroups ?? []).filter(g => g.id !== group.id),
								);
							}}
						/>
					))
					}

					{isReddit() && platformUser
							&& <AgencyAccessListItem
								entity={{
									user: platformUser, agencyIsOwner: false, you: true, __typename: 'AgencyUser',
								}}
							/>
					}

					{values.users?.filter(user => !user?.agencyIsOwner)?.map(user => (
						<AgencyAccessListItem
							key={user?.user?.id}
							entity={{
								...user,
								you: platformUser?.userId === user?.user?.userId,
							}}
							onRemove={() => {
								setFieldValue(
									'users',
									(values.users ?? []).filter(u => u.user?.id !== user?.user?.id),
								);
							}}
						/>
					))
					}
				</div>
			</div>

			<div className="flex flex-col gap-[16px] flex-1 w-[25%]">
				<div className="font-semibold text-[14px]">{t('re: advertiser sidebar title', 'WISE.BLUE')}</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>{t('re: advertiser sidebar id', 'ID')}</div>
						<div>{t('re: advertiser sidebar access', 'Access')}</div>
						<div>{t('re: advertiser sidebar salesforce', 'Salesforce')}</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.agencyAccountId ?? '-'}
						</div>
						<div>
							{values?.agencyAccountId ? <AccessBadge
								accessEntities={
									values?.clients?.map(client => ({
										type: 'CLIENT',
										name: client.clientName ?? client.adAccountName,
									})) ?? []
								}
							/> : '-'
							}
						</div>
						<div>
							{values.salesforceId ? <a
								href={`https://alephholding.my.salesforce.com/${values.salesforceId}`}
								target="_blank"
								className="text-blue hover:text-blue-600"
								rel="noreferrer"
							>
								{t('re: advertiser sidebar open salesforce profile', 'Open profile')}
							</a> : <span className="text-gray-800">-</span>
							}
						</div>
					</div>
				</div>
			</div>

			<div
				className="absolute bottom-[32px] left-[50%] -translate-x-1/2
						flex gap-[16px] p-[24px] shadow-around-sm bg-white rounded-lg"
			>
				{!(loading)
						&& (!showDiscard)
						&& <Button
							title={t('sc: discard', 'Discard')}
							data-testid="advertiser-discard"
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							onClick={beforeClose}
						/>
				}

				<Button
					title={t('re: advertiser create new', 'Create new advertiser')}
					data-testid="advertiser-confirm"
					leftNode={
						loading ? <LoaderBars className="animate-spin" /> : <CheckIcon />
					}
					color={ButtonColor.GREEN}
					disabled={loading || anyInvalidTouched()}
					loading={loading}
					onClick={submitForm}
				/>
			</div>
		</div>
	);
};

export default RedditAdvertiserMutator;
